import {
    AfterViewInit,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { MatMenuTrigger } from '@angular/material/menu';
import { NavigationStart, Router } from '@angular/router';
import { Actions, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Logger, themeEstensaWorklist } from '@se/se.platform.base';
import { AgGridAngular } from 'ag-grid-angular';
import {
    AllCommunityModule,
    CellContextMenuEvent,
    ColDef,
    Column,
    ColumnState,
    FirstDataRenderedEvent,
    GetRowIdFunc,
    GridApi,
    GridOptions,
    GridReadyEvent,
    GridSizeChangedEvent,
    RowDataUpdatedEvent,
    RowDoubleClickedEvent,
    RowNode,
    RowSelectionOptions,
    SelectionChangedEvent,
} from 'ag-grid-community';
import { UserPreferences } from 'estensa-module';
import { forEach, remove, sortBy, sumBy, values } from 'lodash-es';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { RootState } from '../../root-store/root.state';
import {
    MenuEntryChangeEventType,
    MenuSettingsConfiguration,
    SettingsConfiguration,
    SettingsDataValues,
    SingleSettingConfig,
} from '../../se-search-settings/types';
import {
    seSidePatientHistoryComponentNoResult,
    seSidePatientHistoryComponentSetPatient,
} from '../../se-side/se-side-components/se-side-patient-history/redux/se-side-patient-history.actions';
import {
    seToolbarButtonPressed,
    seToolbarButtonToggle,
} from '../../se-toolbar/se-buttons-toolbar/redux/se-buttons-toolbar.actions';
import { SeButtonsToolbarService } from '../../se-toolbar/se-buttons-toolbar/se-buttons-toolbar.service';
import { ToolbarButtonData } from '../../se-toolbar/se-buttons-toolbar/types';
import { EstensaHealthPlatformService } from '../../services/angularjs/health-platform.service';
import { ImagingService } from '../../services/imaging/imaging.service';
import { RadioSettingsService } from '../../services/settings/radio-settings.service';
import { UserConfigurationSettingsService } from '../../services/settings/user-configuration-settings.service';
import { EstensaWaitingService } from '../../services/waiting.service';
import {
    SearchPageActions,
    seSearchCompleted,
    seSearchOpen,
    seSearchRemoveItems,
    seSearchSelect,
    seSearchUnlockReadonly,
    seSearchUpdateCells,
    seSearchUpdateItems,
    seSearchUpdateItemsProps,
} from '../redux/search-page.actions';
import { selectSeSearchDiff } from '../redux/search-page.selectors';
import { SearchPageConfigurationService } from '../search-page-configuration.service';
import { SearchFormType } from '../search-page-type.enum';
import { SearchPageService } from '../search-page.service';
import {
    EstensaColDef,
    EstensaGridSetting,
    EstensaSettingsType,
    PostLoadingSelection,
    SettingDetail,
} from '../search-param.model';
import { AssociatedSpecialtyRendererComponent } from './cell-renderers/associated-specialty-renderer/associated-specialty-renderer.component';
import { AssociatedVisitRendererComponent } from './cell-renderers/associated-visit-renderer/associated-visit-renderer.component';
import { BoolIconRendererComponent } from './cell-renderers/bool-icon-renderer/bool-icon-renderer.component';
import { LockUnlockRendererComponent } from './cell-renderers/lock-unlock-renderer/lock-unlock-renderer.component';
import { PrintingCdStatusRendererComponent } from './cell-renderers/printing-cd-status-renderer/printing-cd-status-renderer.component';
import { SameStudyRendererComponent } from './cell-renderers/same-study-renderer/same-study-renderer.component';
import { StateIconRendererComponent } from './cell-renderers/state-icon-renderer/state-icon-renderer.component';
import { SuccessFailureRendererComponent } from './cell-renderers/success-failure-renderer/success-failure-renderer.component';
import { TooltipCustomRendererComponent } from './cell-renderers/tooltip-custom-renderer/tooltip-custom-renderer.component';
import { DiffHeaderComponent } from './header-renders/diff-header/diff-header.component';
import { ButtonIconRendererComponent } from './cell-renderers/button-icon-renderer/button-icon-renderer.component';

@Component({
    selector: 'se-search-result',
    templateUrl: './search-result.component.html',
    styleUrls: ['./search-result.component.scss'],
    standalone: false,
})
export class SearchResultComponent implements AfterViewInit, OnInit, OnDestroy {
    // Enumerati visibili nell'HTML
    searchFormType = SearchFormType;
    settingType = EstensaSettingsType;
    afterLoadingSelection = PostLoadingSelection;

    // Data members
    gridOptions: GridOptions;
    gridApi: GridApi;
    isFirstRowRendered: boolean = false;
    selectionMetaData: ISelectionButtonMetadata[] = [];
    contextMenuPosition = { x: '0px', y: '0px' };
    contextMenuButtonsData: ToolbarButtonData[] = [];
    maxEcgToOpenTogether?: number;
    showMaxSelectionDialog = false;
    selectAfterUnlock: boolean = false;
    menuSettingsConfig: SettingsConfiguration = [
        {
            menuName: 'ColumnVisibilityManagement',
            nameTranslationKey: 'ColumnVisibilityManagement',
            settings: [],
        },
    ];
    menuSettingsData: SettingsDataValues[] = [{}];
    columnDefs: EstensaColDef[] = [];
    localeText = { noRowsToShow: this.translate.instant('NoRowsToShow') };
    defaultColDef = { editable: false, sortable: true, resizable: true };
    components?: { [p: string]: any } = {};
    context: any;
    rowHeight: number;

    @ViewChild('datagrid')
    datagrid: AgGridAngular;

    @ViewChild('worklistContextMenuTrigger') contextMenu: MatMenuTrigger;

    @Input()
    showPrimaryColSetting: boolean = false;

    @Input()
    formType: SearchFormType;

    @Input()
    items: Array<any>;

    @Input()
    idPropertyName: string; // Nome della proprietà che contiene l'identificativo univoco di un oggetto che rappresetna il dato dcontenuto in una riga

    @Input()
    disabledShowHideIdList: string[]; // Lista degli id relativi alle colonne che non devono essere inseriti nel menu' Show\Hide

    @Input()
    enableAlternateRowBackground: boolean = true;

    @Input()
    postLoadingSelectionOps: PostLoadingSelection;

    @Input()
    rowSelection: RowSelectionOptions;

    @Input()
    keepSelectionAfterSearch = false;

    @Input()
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    canItemBeSelected: Function = (itemToSelect, actualSelectedItems) => {
        return true;
    };

    @Input()
    canPatientHistoryOpenForSelection;

    @Input()
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    canItemDoubleClicked: Function = (itemDoubleClicked, actualSelectedItems) => {
        return true;
    };

    @Input()
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    getTooltipForSelection: Function = (row) => {
        return null;
    };

    @Input()
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    getButtonContentForSelection: Function = (row) => {
        return null;
    };

    @Input()
    isBusy: boolean = true;

    @Input()
    searchOnInit: boolean = true;

    @Output()
    unlockWorklistItemRequired: EventEmitter<RowNode> = new EventEmitter<RowNode>();

    @Output()
    primaryChanged: EventEmitter<{ colId: string; primary: boolean }> = new EventEmitter<{
        colId: string;
        primary: boolean;
    }>();

    @Output()
    visibilityChanged: EventEmitter<{ colId: string; isVisible: boolean }> = new EventEmitter<{
        colId: string;
        isVisible: boolean;
    }>();

    get currentColumnDefsForVisibility(): EstensaColDef[] {
        // La visibilità per il locked non la impostiamo
        switch (this.formType) {
            case SearchFormType.EXECUTION: {
                const sortedCols = sortBy(this.searchPageConfigurationService.getAllColumnDefs(this.formType), [
                    'headerName',
                ]);
                const cols = remove(sortedCols, (col) => !this.disabledShowHideIdList.includes(col.colId));

                return cols as EstensaColDef[];
            }
            default: {
                const sortedCols = sortBy(this.searchPageConfigurationService.getColumnDefs(this.formType), [
                    'headerName',
                ]);
                const cols = remove(sortedCols, (col) => !this.disabledShowHideIdList.includes(col.colId));

                return cols as EstensaColDef[];
            }
        }
    }

    private routerSubscription: Subscription;

    private actionsSubscription: Subscription;

    private lastSelectionSubscription: Subscription;

    private CRUDSubscription: Subscription;

    private searchCompletedSubscription: Subscription;

    private buttonsToolbarSubscription: Subscription;

    agGridModuls = [AllCommunityModule];
    agGridTheme = themeEstensaWorklist;

    getRowId: GetRowIdFunc<any> = (params) => params.data.id ?? '';

    // Constructor

    constructor(
        private searchPageConfigurationService: SearchPageConfigurationService,
        private searchPageService: SearchPageService,
        private translate: TranslateService,
        private router: Router,
        private waitingService: EstensaWaitingService,
        private userConfigurationSettingsService: UserConfigurationSettingsService,
        private actions: Actions,
        private store: Store<RootState>,
        private imagingService: ImagingService,
        private healthPlatformService: EstensaHealthPlatformService,
        private settingsService: RadioSettingsService,
        private logger: Logger,
    ) {}

    ngOnInit() {
        this.listenForStoreEvents();

        this.columnDefs = this.searchPageConfigurationService.getColumnDefs(this.formType);
        this.components = {
            diffHeaderRenderer: DiffHeaderComponent,
            boolIconRenderer: BoolIconRendererComponent,
            buttonIconRenderer: ButtonIconRendererComponent,
            stateIconRenderer: StateIconRendererComponent,
            lockUnlockRenderer: LockUnlockRendererComponent,
            successFailureRenderer: SuccessFailureRendererComponent,
            printingCdRenderer: PrintingCdStatusRendererComponent,
            associatedVisitsRenderer: AssociatedVisitRendererComponent,
            sameStudyRenderer: SameStudyRendererComponent,
            customTooltipComponent: TooltipCustomRendererComponent,
            associatedSpecialtyRenderer: AssociatedSpecialtyRendererComponent,
        };
        this.context = {
            diff$: this.store.pipe(select(selectSeSearchDiff(this.formType))),
            unlockWorklistItem: this.unlockWorklistItem.bind(this),
            canUnlockWorklistItem: this.userConfigurationSettingsService.isAuthorized(
                ['examination_LockUnlock'],
                'single',
            ),
            associatedIconClick: this.associatedIconClick.bind(this),
        };
        this.maxEcgToOpenTogether = this.settingsService.radioSettings?.ecgSettings.maxEcgToOpenTogether;

        /*configurazione nuovo settings menu*/
        const cols = this.currentColumnDefsForVisibility;
        const menuSettings: SingleSettingConfig[] = [
            {
                name: 'visibility',
                type: 'boolean',
                defaultValue: true,
                nameTranslationKey: 'visibility',
                icons: [
                    {
                        icon: 'visibility',
                        isPrefix: null,
                        isSuffix: null,
                        showMatchValue: true,
                    },
                    {
                        icon: 'visibility_off',
                        isPrefix: null,
                        isSuffix: null,
                        showMatchValue: false,
                    },
                ],
                classes: [
                    {
                        className: 'color-primary',
                        showMatchValue: true,
                    },
                    {
                        className: 'color-secondary',
                        showMatchValue: false,
                    },
                ],
                tooltip: 'toggle visibility',
            },
        ];
        if (this.showPrimaryColSetting)
            menuSettings.push({
                name: 'primary',
                type: 'boolean',
                defaultValue: true,
                nameTranslationKey: 'IsPrimary',
                icons: [],
                classes: [],
                tooltip: 'toggle primary',
            });
        this.menuSettingsConfig[0].settings = this.colDefToMenuSettings(cols, menuSettings);
    }

    colDefToMenuSettings = (
        colDefs: EstensaColDef[],
        settings: SingleSettingConfig[] = [
            {
                name: 'visibility',
                type: 'boolean',
                defaultValue: true,
                nameTranslationKey: 'visibility',
                icons: [
                    {
                        icon: 'visibility',
                        isPrefix: null,
                        isSuffix: null,
                        showMatchValue: true,
                    },
                    {
                        icon: 'visibility_off',
                        isPrefix: null,
                        isSuffix: null,
                        showMatchValue: false,
                    },
                ],
                classes: [
                    {
                        className: 'color-primary',
                        showMatchValue: true,
                    },
                    {
                        className: 'color-secondary',
                        showMatchValue: false,
                    },
                ],
                tooltip: 'toggle visibility',
            },
        ],
    ): MenuSettingsConfiguration[] => {
        return colDefs.map((col) => {
            return {
                id: col.colId,
                name: col.headerName || col.colId,
                tooltip: col.headerTooltip || '',
                settings,
            };
        });
    };

    ngAfterViewInit() {
        this.waitingService.isWaiting = false;
    }

    ngOnDestroy() {
        this.routerSubscription.unsubscribe();
        this.actionsSubscription.unsubscribe();
        this.lastSelectionSubscription.unsubscribe();
        this.CRUDSubscription.unsubscribe();
        this.searchCompletedSubscription.unsubscribe();
        this.buttonsToolbarSubscription.unsubscribe();

        // Libero risorse che potrebbero generare dei leaks
        this.gridApi = null;
        this.gridOptions = null;
    }

    listenForStoreEvents() {
        this.routerSubscription = this.router.events
            .pipe(filter((event) => event instanceof NavigationStart))
            .subscribe((event: NavigationStart) => {
                if (event.url == '/menu') {
                    // Pulisco tutti gli stati parziali delle colonne
                    this.searchPageConfigurationService.resetAllActualConfigurations();
                } else
                    /* Se sto andando in una pagina diversa dal menu principale, salvo lo stato di questa search panel */
                    this.searchPageConfigurationService.storeGridActualState(
                        this.gridApi.getColumnState(),
                        this.formType,
                    );
            });

        this.actionsSubscription = this.actions.pipe(ofType(seSearchUpdateCells)).subscribe((a) => {
            this.gridApi.refreshCells(a.params);
        });

        this.lastSelectionSubscription = this.searchPageService
            .getSelectedItems$(this.formType, this.idPropertyName)
            .subscribe((rows) => {
                this.selectionMetaData = [];

                if (rows) {
                    this.selectionMetaData.push(
                        ...rows.map((row, index) => {
                            index++;
                            return {
                                id: row[this.idPropertyName],
                                tooltip: this.getTooltipForSelection(row)
                                    ? this.getTooltipForSelection(row)
                                    : this.translate.instant('Selection') + ' ' + index,
                                buttonContent: this.getButtonContentForSelection(row)
                                    ? this.getButtonContentForSelection(row)
                                    : index,
                            };
                        }),
                    );
                } 
            });

        this.buttonsToolbarSubscription = this.searchPageService.getToolbarButtons().subscribe((buttons) => {
            this.contextMenuButtonsData.splice(0, this.contextMenuButtonsData.length);
            if (this.formType === SearchFormType.EXECUTION) {
                this.contextMenuButtonsData.push(
                    ...values(buttons).filter(
                        (b) =>
                            (b.status.visible && !b.status.disabled && b.status.visibleIntoContext) ||
                            (b.status.visible &&
                                !b.status.disabled &&
                                b.status.visibleIntoContext &&
                                !b.status.visibleIntoToolbar),
                    ),
                );
            } else {
                this.contextMenuButtonsData.push(
                    ...values(buttons).filter((b) => b.status.visible && !b.status.disabled),
                );
            }
        });

        this.searchCompletedSubscription = this.actions
            .pipe(
                ofType(seSearchCompleted),
                filter((action) => action.formType === this.formType),
            )
            .subscribe(({ addedRows, highlightRow, oldSelectedItems }) => {
                // Faccio la differenza tra i vecchi e i nuovi item, e da quelli recuper le corrispondenti righe della griglia, recuperando così le
                //  righe che sono state aggiunte.
                if (highlightRow) {
                    this.flashRows(addedRows);
                }

                if (this.keepSelectionAfterSearch) {
                    this.manualSelection(oldSelectedItems);
                } else {
                    this.store.dispatch(
                        seSearchSelect({
                            formType: this.formType,
                            items: [],
                            rows: [],
                        }),
                    );
                    this.store.dispatch(
                        seSidePatientHistoryComponentSetPatient({
                            formType: this.formType,
                            patientId: null,
                        }),
                    );
                }
            });

        this.CRUDSubscription = this.actions
            .pipe(ofType(seSearchUpdateItemsProps, seSearchUpdateItems, seSearchRemoveItems))
            .subscribe((a) => {
                setTimeout(async () => {
                    switch (a.type) {
                        case SearchPageActions.UPDATE_ITEMS:
                            // Recupero vecchia selezione
                            this.manualSelection(await this.searchPageService.getSelectedItemIds(this.formType));
                            // Esegue flashing righe se richiesto
                            if (a.highlightRow) this.flashRows(a.items);
                            break;
                        case SearchPageActions.REMOVE_ITEMS:
                            // Recupero vecchia selezione
                            this.manualSelection(await this.searchPageService.getSelectedItemIds(this.formType));
                            break;
                        case SearchPageActions.UPDATE_ITEMS_PROP:
                            if (a.prop == 'locked') {
                                // Se faccio la selezione di tutto recupero anche la vecchia selezione
                                // se si volesse la selezione  singola (con la elena abbiamo detto di no)
                                // allora usare la riga sotto (si puo' commentare anche la select prima della unlock)
                                this.manualSelection(await this.searchPageService.getSelectedItemIds(this.formType));
                                //this.manualSelection(a.items, false);
                            }
                            break;
                        default:
                            // Do nothing
                            break;
                    }
                });
            });
    }

    private manualSelection(itemIds: string[], ensureIndexVisible: boolean = true) {
        if (!itemIds || itemIds.length < 1) {
            this.isFirstRowRendered = true;
            return;
        }
        this.isFirstRowRendered = false;
        const itemToShow = itemIds[0];

        this.gridApi.forEachNodeAfterFilterAndSort((node) => {
            // Seleziona il nodo, se è da selezionare
            for (const id of itemIds) {
                if (id == node.data[this.idPropertyName]) {
                    node.setSelected(true);
                }
            }
            // Scorri fino al primo elemento selezionato
            if (ensureIndexVisible && itemToShow == node.data[this.idPropertyName]) {
                this.gridApi.ensureIndexVisible(node.rowIndex, 'middle');
            }
        });
    }

    private manualSelectionNoScroll(itemIds: string[]) {
        if (!itemIds || itemIds.length < 1) {
            return;
        }

        this.gridApi.forEachNodeAfterFilterAndSort((node) => {
            // Seleziona il nodo, se è da selezionare
            for (const id of itemIds) {
                if (id == node.data[this.idPropertyName]) {
                    node.setSelected(true);
                }
            }
        });
    }

    private flashRows(items: any[]) {
        this.gridApi.forEachNodeAfterFilterAndSort((node, index) => {
            items.forEach((item) => {
                if (node.data[this.idPropertyName] == item[this.idPropertyName]) {
                    this.gridApi.flashCells({
                        rowNodes: [this.gridApi.getDisplayedRowAtIndex(index)],
                        flashDuration: 2000,
                        fadeDuration: 1000,
                    });
                }
            });
        });
    }

    onToggleColumnVisibility = (colDef: ColDef, e: MouseEvent) => {
        e.stopPropagation();
        this.gridApi.setColumnsVisible([colDef.colId ?? ''], !this.isColumnVisible(colDef));
        this.menuSettingsData[0][colDef.colId ?? ''] = {
            ...this.menuSettingsData[0][colDef.colId ?? ''],
            visibility: !!this.isColumnVisible(colDef),
        };
        this.menuSettingsData = [...this.menuSettingsData];
    };

    onGridSizeChanged(sizeEvent: GridSizeChangedEvent) {
        let clientWidth = sizeEvent.clientWidth;
        let columns = sizeEvent.api.getAllDisplayedColumns();
        let usedWidth = sumBy(columns, (col) => col.getActualWidth());

        if (usedWidth < clientWidth) {
            sizeEvent.api.sizeColumnsToFit();
        }
        this.changeRowHeight(window.innerWidth);
    }

    async onRowDataUpdated(_: RowDataUpdatedEvent) {
        // Quando c'è un datachange svuoto il viewer...
        this.imagingService.discardImages();

        // Chiudo eventuali tab del telemonitoraggio rimasti aperti
        this.healthPlatformService.closePage();

        // ...e seleziono le righe erano selezionate prima.
        // In questo caso ignoriamo postLoadingSelectionOps perché l'evento viene scatenato anche quando cambia una
        //   sola colonna.
        this.manualSelection(await this.searchPageService.getSelectedItemIds(this.formType));
    }

    /**
     * Se SelectFirstItem = true, al primo caricamento seleziono il primo item
     * this.gridApi.forEachNodeAfterFilterAndSort(node => (node.rowIndex ? 0 : node.setSelected(true))); // seleziona la prima riga
     */
    onFirstDataRendered(_: FirstDataRenderedEvent) {
        if (this.postLoadingSelectionOps !== PostLoadingSelection.firstItem) {
            this.store.dispatch(
                seSidePatientHistoryComponentNoResult({
                    formType: this.formType,
                    message: this.translate.instant('NoResults'),
                }),
            );
        }
        if (this.formType === SearchFormType.EXECUTION && this.isFirstRowRendered) {
            let firstRow = this.gridApi.getDisplayedRowAtIndex(0);
            firstRow?.setSelected(true);
            this.isFirstRowRendered = false;
        }
    }

    /**
     *  Salva la configurazione attuale della griglia sul setting utente\sito a seconda del valore del
     *  parametro passato (scrive sul setting)
     * @param settingsType
     */
    onSaveColumnConfig = (settingsType: EstensaSettingsType) => {
        switch (this.formType) {
            case SearchFormType.EXECUTION:
                {
                    let columnDef: EstensaColDef[] = this.searchPageConfigurationService.getAllColumnDefs(
                        this.formType,
                    );
                    let propToSave: SettingDetail[] = this.assignSettingsProp(columnDef);
                    this.searchPageConfigurationService.storeAndSaveGridState(
                        this.gridApi.getColumnState(),
                        this.formType,
                        settingsType,
                        propToSave,
                    );
                }
                break;
            default:
                this.searchPageConfigurationService.storeAndSaveGridState(
                    this.gridApi.getColumnState(),
                    this.formType,
                    settingsType,
                );
        }
    };

    private getGridSettingName() {
        switch (this.formType) {
            case SearchFormType.ECG:
                return 'ecgWorklistGrid';
            case SearchFormType.VISIT:
                return 'visitWorklistGrid';
            case SearchFormType.COMPLETE_CD:
                return 'completeCdWorklistGrid';
            case SearchFormType.ON_PRINTING_CD:
                return 'onPrintingCdWorklistGrid';
            case SearchFormType.TELEMONITORING:
                return 'telemonitoringWorklistGrid';
            case SearchFormType.NRE:
                return 'nreWorklistGrid';
            case SearchFormType.DUMMY_VNEXT2:
                return 'executionWorklistGrid';
            case SearchFormType.EXECUTION:
                return 'executionWorklistGrid';
            case SearchFormType.SPECIALTY:
                return 'specialtyWorklistGrid';
        }

        return '';
    }

    /**
     *  Richiama la configurazione di fabbrica della griglia, la applica e la salva nel setting utente\sito
     *  a seconda del valore del parametro passato (scrive sul setting)
     * @param settingsType
     */
    onResetConfigurationColumn = (settingsType: EstensaSettingsType) => {
        switch (this.formType) {
            case SearchFormType.EXECUTION:
                this.onResetDefaultColumnConfigEx(settingsType);
                break;

            default:
                this.onResetDefaultColumnConfig(settingsType);
        }
    };

    onResetDefaultColumnConfig = (settingsType: EstensaSettingsType) => {
        if (settingsType == EstensaSettingsType.site) {
            let factoryColumnState = this.searchPageConfigurationService.getFactoryColumnState(this.formType);
            if (factoryColumnState) {
                this.gridApi.applyColumnState({ state: factoryColumnState, applyOrder: true });
                this.updateColSettingsDataVisibility(this.searchPageConfigurationService.getColumnDefs(this.formType));
            }

            this.onSaveColumnConfig(settingsType);
        } else if (settingsType == EstensaSettingsType.user) {
            this.searchPageConfigurationService
                .getDefaultConfigurationUserPreferences()
                .subscribe((preferences: UserPreferences) => {
                    let userFactoryColumnState = null;
                    try {
                        let p = preferences.gridUserPreference[this.getGridSettingName()];
                        userFactoryColumnState = JSON.parse(p);
                    } catch {
                        userFactoryColumnState = null;
                    }
                    if (userFactoryColumnState)
                        this.gridApi.applyColumnState({ state: userFactoryColumnState, applyOrder: true });
                    let actualColumns: ColDef[] = this.gridApi.getColumnDefs();
                    const cols = remove(actualColumns, (col) => !this.disabledShowHideIdList.includes(col.colId));
                    this.updateColSettingsDataVisibility(cols);
                    this.onSaveColumnConfig(settingsType);
                });
        }
    };

    //Nuovo metodo richiamato nello switch onResetConfigurationColumn
    onResetDefaultColumnConfigEx = (settingsType: EstensaSettingsType) => {
        if (settingsType == EstensaSettingsType.site) {
            let factoryColumnState: ColumnState[] = this.searchPageConfigurationService.getFactoryColumnState(
                this.formType,
            );
            let factoryColumnDef: EstensaColDef[] = this.searchPageConfigurationService.getDefaultColumnDefs(
                this.formType,
            );
            let detailsSettingFactory: SettingDetail[] = this.assignSettingsProp(factoryColumnDef);

            if (factoryColumnState.length != 0) {
                this.searchPageConfigurationService.updateGrid(
                    this.gridApi,
                    this.formType,
                    detailsSettingFactory,
                    factoryColumnState,
                );
                this.updateColSettingsDataVisibility(this.currentColumnDefsForVisibility);
                this.onSaveColumnConfig(settingsType);
            }
        } else if (settingsType == EstensaSettingsType.user) {
            this.searchPageConfigurationService
                .getDefaultConfigurationUserPreferences()
                .subscribe((preferences: UserPreferences) => {
                    let userFactoryColumnStatus: EstensaGridSetting[] = [];
                    try {
                        let p = preferences.gridUserPreference[this.getGridSettingName()];
                        userFactoryColumnStatus[0] = JSON.parse(p);
                    } catch {
                        userFactoryColumnStatus = [];
                    }

                    if (userFactoryColumnStatus.length != 0) {
                        this.searchPageConfigurationService.updateGrid(
                            this.gridApi,
                            this.formType,
                            userFactoryColumnStatus[0].details,
                            userFactoryColumnStatus[0].colsState,
                        );
                        this.updateColSettingsDataVisibility(this.currentColumnDefsForVisibility);
                        this.onSaveColumnConfig(settingsType);
                    }
                });
        }
    };

    private assignSettingsProp(colDef: EstensaColDef[]): SettingDetail[] {
        let details = colDef.map((col: EstensaColDef) => {
            let setting: SettingDetail = {
                colId: col.colId,
                hide: col.hide,
                estensa: { primary: col?.context.estensaDetails?.primary },
            };
            return setting;
        });
        return details;
    }

    // #region OnGridReady

    onGridReady(evt: GridReadyEvent) {
        this.gridApi = evt.api;
        this.initGridWithSettings(evt);
        this.gridApi.sizeColumnsToFit();
    }

    private initGridWithSettings(evt: GridReadyEvent) {
        switch (this.formType) {
            case SearchFormType.EXECUTION:
                this.initGridEstensaColDef(evt);
                break;
            default:
                this.initWithColumnState(evt);
        }
    }

    //nuova init che gestisce eventuali setting
    private initGridEstensaColDef(evt: GridReadyEvent) {
        this.gridApi = evt.api;
        let columnState: ColumnState[] = this.gridApi.getColumnState();
        let columnDef: EstensaColDef[] = this.searchPageConfigurationService.getAllColumnDefs(this.formType);

        this.searchPageConfigurationService.saveGridFactoryStatus(columnState, columnDef, this.formType);

        const gridSettings: EstensaGridSetting[] = this.searchPageConfigurationService.getGridSettings(this.formType);

        if (gridSettings[0].details?.length != 0 && gridSettings[0].colsState?.length != 0) {
            this.searchPageConfigurationService.updateGrid(
                evt.api,
                this.formType,
                gridSettings[0].details,
                gridSettings[0].colsState,
            );
            this.updateColSettingsDataVisibility(this.currentColumnDefsForVisibility);
        } else {
            this.gridApi.applyColumnState({ state: columnState, applyOrder: true });
            this.updateColSettingsDataVisibility(this.currentColumnDefsForVisibility);
        }
    }

    // #endregion

    private initWithColumnState(evt: GridReadyEvent) {
        this.gridApi = evt.api;
        let columnState: ColumnState[] = this.gridApi.getColumnState();
        this.searchPageConfigurationService.storeGridFactoryState(columnState, this.formType);

        const _columnState = this.searchPageConfigurationService.getColumnState(this.formType);
        columnState = _columnState?.length ? _columnState : columnState;
        if (columnState?.length) {
            this.gridApi.applyColumnState({ state: columnState, applyOrder: true });
            let actualColumns: ColDef[] = this.gridApi.getColumnDefs() ?? [];
            const cols = remove(actualColumns, (col) => !this.disabledShowHideIdList.includes(col.colId ?? ''));
            this.updateColSettingsDataVisibility(cols);
        }
    }

    updateColSettingsDataVisibility = (columnDef: EstensaColDef[]) => {
        this.menuSettingsData[0] = Object.fromEntries(
            columnDef.map((col) => {
                return this.showPrimaryColSetting
                    ? [
                          col.colId,
                          {
                              visibility: !col.hide,
                              primary: Boolean(col.context.estensaDetails?.primary),
                          },
                      ]
                    : [col.colId, { visibility: !col.hide }];
            }),
        );
        this.menuSettingsData = [...this.menuSettingsData];
    };

    onSelectedItemsChange(evt: SelectionChangedEvent) {
        try {
            const oldSelectedItems = this.searchPageService.getSelectedItems(this.formType, this.idPropertyName);
            let newSelectedNodes = evt.api.getSelectedNodes();

            if (
                this.maxEcgToOpenTogether !== undefined &&
                this.formType == SearchFormType.ECG &&
                newSelectedNodes.length > this.maxEcgToOpenTogether
            ) {
                this.showMaxSelectionDialog = true;
                evt.api.deselectAll();
            }

            newSelectedNodes.sort((a, b) => {
                if (a.rowIndex > b.rowIndex) return 1;

                return -1;
            });

            const newSelectableItems = [];
            for (const node of newSelectedNodes) {
                if (this.canItemBeSelected(node.data, oldSelectedItems)) {
                    newSelectableItems.push(node.data);
                } else {
                    node.setSelected(false, true);
                }
            }

            setTimeout(() => {
                this.selectionChanged(newSelectableItems, false);
            });
        } catch (err) {
            this.logger.error(err);
        }
    }

    async onRowDoubleClick(e: RowDoubleClickedEvent) {
        try {
            // Deseleziono tutto - sul doppio click prendo solo l'item selezionato
            if (this.rowSelection.mode == 'multiRow') {
                const nodes = e.api.getSelectedNodes();
                forEach(nodes, (node) => {
                    if (node.id != e.node.id) node.setSelected(false, true);
                });
            }

            // Se è selezionabile
            if (
                this.canItemBeSelected(
                    e.data,
                    await this.searchPageService.getSelectedItems(this.formType, this.idPropertyName),
                )
            ) {
                if (!e.node.isSelected()) {
                    e.node.setSelected(true, false);
                }
            } else {
                e.node.setSelected(false, true);
                return;
            }
            // seleziono e apro solo se è 'doppiocliccabile' altrimenti apro solo
            const open = this.canItemDoubleClicked(
                e.data,
                await this.searchPageService.getSelectedItems(this.formType, this.idPropertyName),
            );

            let rows = e.api.getSelectedRows();

            setTimeout(() => {
                this.selectionChanged(rows, open);
            });
        } catch {
            /* In che casi può fallire? */
        }
    }

    private selectionChanged(selectedRows: any[], open: boolean) {
        this.store.dispatch(
            seSearchSelect({
                formType: this.formType,
                items: selectedRows.map((i) => i[this.idPropertyName]),
                rows: selectedRows,
            }),
        );
        if (open) {
            this.store.dispatch(seSearchOpen({ rows: selectedRows }));
        } else {
            // non è una open, ma solo una selezione
            if (
                !!selectedRows[0] &&
                !!selectedRows[0]['patient'] &&
                this.canPatientHistoryOpenForSelection(selectedRows)
            ) {
                // C'è una selezione singola, ma la blocco con una logica custom (eg. i fuori flusso)
                this.store.dispatch(
                    seSidePatientHistoryComponentSetPatient({
                        formType: this.formType,
                        patientId: selectedRows[0]['patient'].id,
                    }),
                );
                //this.store.dispatch(
                //    seTimelineComponentSetPatient({
                //        formType: this.formType,
                //        patientId: selectedRows[0]['patient'].id,
                //    }),
                //);
            } else {
                //this.store.dispatch(
                //    seSidePatientHistoryComponentSetPatient({
                //        formType: this.formType,
                //        patientId: null
                //    }),
                //);
                this.store.dispatch(
                    seSidePatientHistoryComponentNoResult({
                        formType: this.formType,
                        message:
                            selectedRows && selectedRows.length > 1
                                ? this.translate.instant('MultiSelectioHistoryNotAvailable')
                                : this.translate.instant('NoResults'),
                    }),
                );
            }
        }
    }

    onClickOutsideMenu(event$?: any) {
        if (!event$.hasAttribute('se-mat-menu')) {
            this.contextMenu?.closeMenu();
        }
    }

    onContextMenu(event: any) {
        event.preventDefault();
    }

    onCellContextMenu(event: CellContextMenuEvent) {
        const Rowselected = event.data;

        event.api.forEachNode((node) => {
            if (Rowselected.id == node.data.id) node.setSelected(true, true);
        });

        if (!!this.contextMenuButtonsData && this.contextMenuButtonsData.length > 0) {
            this.contextMenuPosition.x = (event.event as MouseEvent).clientX - 3 + 'px';
            this.contextMenuPosition.y = (event.event as MouseEvent).clientY - 3 + 'px';
            this.contextMenu.openMenu();
        }
    }

    onContextMenuAction(button: ToolbarButtonData) {
        if (button.isToggle) {
            // Se è un toggle carico a mano il css
            const checked = SeButtonsToolbarService.toggleToolbarButton(button.actionType, this.contextMenuButtonsData);
            this.store.dispatch(seToolbarButtonToggle({ button: button.actionType, checked: checked }));
        } else {
            this.store.dispatch(seToolbarButtonPressed({ button: button.actionType }));
        }
    }

    isColumnVisible = (colDef: EstensaColDef) => {
        if (this.gridApi) {
            let c: Column | null = this.gridApi.getColumn(colDef.colId);
            return c?.isVisible();
        }
        return !colDef.hide;
    };

    isCurrentUserAuthorized = (): boolean => {
        return this.userConfigurationSettingsService.isAuthorized(['configurationForSite_Set'], 'single');
    };

    unlockWorklistItem($event: UIEvent, data: any) {
        $event.preventDefault();

        // Se la riga non è selezionata la aggiungo alla selezione esistente
        // Coma da accordi con la elena aggiungiamo la selezione della unlock a quella
        // esistente
        const selectedNodes = this.gridApi
            .getSelectedNodes()
            .filter((node) => node.data[this.idPropertyName] == data.id);
        if (selectedNodes.length < 1) this.manualSelection([data.id], false);

        this.selectAfterUnlock = true;
        this.store.dispatch(seSearchUnlockReadonly({ formType: this.formType, items: [data] }));
    }

    selectionBtnClick = (data: ISelectionButtonMetadata) => {
        const selectedNodes = this.gridApi
            .getSelectedNodes()
            .filter((node) => node.data[this.idPropertyName] == data.id);
        if (selectedNodes.length > 0) {
            this.gridApi.ensureIndexVisible(selectedNodes[0].rowIndex, 'middle');
        }
    };

    removeSelectionBtnClick = ($event: MouseEvent, data: ISelectionButtonMetadata) => {
        $event.stopPropagation();
        const selectedNodes = this.gridApi
            .getSelectedNodes()
            .filter((node) => node.data[this.idPropertyName] == data.id);
        selectedNodes[0]?.setSelected(false);

        this.store.dispatch(seSidePatientHistoryComponentSetPatient({ formType: this.formType, patientId: null }));
    };

    unselectAllBtnClick = () => {
        this.gridApi.deselectAll();
    };

    private changeRowHeight(screenWidth: number): void {
        if (screenWidth > 1281 && screenWidth < 1440 && this.rowHeight !== 43) {
            this.rowHeight = 43;
            this.gridApi.resetRowHeights();
        } else if (screenWidth > 801 && screenWidth < 1280 && this.rowHeight !== 38) {
            this.rowHeight = 38;
            this.gridApi.resetRowHeights();
        } else if ((screenWidth > 1441 || screenWidth <= 801) && this.rowHeight !== 48) {
            this.rowHeight = 48;
            this.gridApi.resetRowHeights();
        }
    }

    handleChangeConfigEntry({ menuName, propName, settingName, value }: MenuEntryChangeEventType) {
        let propertyExist = this.menuSettingsData?.[0]?.[propName];
        if (!propertyExist) {
            console.error(this.menuSettingsData, { menuName, propName, settingName, value });
            return;
        }
        if (menuName === 'ColumnVisibilityManagement') {
            if (settingName === 'visibility') {
                this.menuSettingsData[0][propName][settingName] = !value;
                this.gridApi.setColumnsVisible([propName], Boolean(!value));
                this.visibilityChanged.emit({ colId: propName, isVisible: Boolean(!value) });
            }
            if (this.showPrimaryColSetting && settingName === 'primary') {
                this.menuSettingsData[0][propName][settingName] = value;
                this.primaryChanged.emit({ colId: propName, primary: Boolean(value) });
            }
            this.gridApi.sizeColumnsToFit();
        }
        this.menuSettingsData = [...this.menuSettingsData];
        if (this.formType === SearchFormType.EXECUTION) {
            this.searchPageConfigurationService.updateGridPrimary(this.gridApi, this.formType);
            const colState = this.gridApi.getColumnState();
            this.gridApi.applyColumnState({ state: colState, applyOrder: true });
            this.gridApi.autoSizeAllColumns();
            this.gridApi.sizeColumnsToFit();
        }
    }

    associatedIconClick(event: UIEvent, data: any) {
        // this.items contiene tutti gli elementi filtrati presenti in pagina
        // data è la riga in cui si clicca l'icona
        let filteredIds = this.items.filter((el) => el.idReport === data.idReport).map((el) => el.id);
        this.unselectAllBtnClick();
        this.manualSelectionNoScroll(filteredIds);
    }
}

export interface ISelectionButtonMetadata {
    id: string;
    tooltip: string;
    buttonContent: any;
}
